import React, { useRef, useState } from 'react';
import { BookingState } from '../../modules/booking-state/BookingState';
import { useModalManager } from '../../modules/contexts/modal-manager-context/ModalManagerProvider';
import { ContactMethodType, Title } from '../../modules/entities/Entities';
import { SelectFieldOption } from '../../modules/forms/fields/SelectFieldOption';
import { CustomerContactPageContext } from './CustomerContactPageContext';
import { CustomerContactPageFormData } from './CustomerContactPageFormData';
import { PrivacyStatementModalContent } from './privacy-statement-modal-content/PrivacyStatementModalContent';
import { Wizard, WizardNavigate } from '../../modules/wizard/Wizard';
import { withPageContext } from '../../modules/contexts/WithPageContext';
import { useViewProvider } from '../../modules/contexts/view-provider-context/ViewProvider';
import { FormRef } from '../../modules/forms/Form';
import { Steps } from '../../modules/wizard/Steps';
import { useBookingContext } from '../../modules/booking-state/UseBookingContext';
import { CustomerContactPageViewProps } from './CustomerContactpageViewProps';
import { maskEmail, maskPhoneNumber } from "../../utils/MaskUtils";
import { mapTitle } from "../../utils/MapUtils";

function createPrefixOptions(): SelectFieldOption[] {
  return Object.values(Title).map((prefix) => ({
    label: prefix,
    value: prefix,
    key: prefix,
  }));
}

function createPreferredContactMethodOptions(): SelectFieldOption[] {
  return Object.values(ContactMethodType).map((method) => ({
    label: method,
    key: method,
    value: method,
  }));
}

function ContextAccessor({
  vehicleDetails,
  vehicleServiceHistory,
  serviceRequest,
  selectedDealer,
  selectedSlot,
  customerContact,
}: BookingState): CustomerContactPageContext | null {
  if (
    !(
      selectedSlot &&
      vehicleDetails &&
      vehicleServiceHistory &&
      selectedDealer &&
      serviceRequest
    )
  ) {
    return null;
  }

  return {
    vehicleDetails,
    customerContact,
  };
}

export const CustomerContactPage = withPageContext(ContextAccessor, ({ pageContext }) => {
  const viewProvider = useViewProvider();
  const [bookingState, setBookingContext] = useBookingContext();
  const [initialFormData] = useState<CustomerContactPageFormData>((): CustomerContactPageFormData => {
    if (pageContext.customerContact) {
      return {
        title: mapTitle(pageContext.customerContact?.title) ?? Title.Dr,
        name: pageContext.customerContact.name,
        surname: pageContext.customerContact.surname,
        email: maskEmail(pageContext.customerContact.email),
        phoneNumber: maskPhoneNumber(pageContext.customerContact.phoneNumber),
        preferredContactMethodType: pageContext.customerContact.preferredContactMethodType,
        privacyStatementConfirmed: true,
      };
    }

    return {
      title: mapTitle(pageContext.vehicleDetails.title) ?? Title.Dr,
      name: pageContext.vehicleDetails.firstName ?? '',
      surname: pageContext.vehicleDetails.lastName ?? '',
      email: maskEmail(pageContext.vehicleDetails.email) ?? '',
      phoneNumber: maskPhoneNumber(pageContext.vehicleDetails.mobile) ?? '',
      preferredContactMethodType: ContactMethodType.SMS,
      privacyStatementConfirmed: false,
    };
  });
  const modalManager = useModalManager();
  const formRef = useRef<FormRef<CustomerContactPageFormData>>(null);
  const [prefixes] = useState(() => createPrefixOptions());
  const [preferredContactMethods] = useState(() => createPreferredContactMethodOptions());

  const privacyStatementModal = PrivacyStatementModalContent();

  const isLoading = !pageContext.customerContact && !pageContext.vehicleDetails;

  const onNext: WizardNavigate = (nextStep) => {
    formRef.current!.submit((data) => {
      const customerContact = {
        name: data.name,
        surname: data.surname,
        email: data.email.includes('*')
          ? (pageContext.customerContact?.email ?? pageContext.vehicleDetails.email)
          : data.email,
        phoneNumber: data.phoneNumber.includes('*')
          ? (pageContext.customerContact?.phoneNumber ?? pageContext.vehicleDetails.mobile)
          : data.phoneNumber,
        preferredContactMethodType: data.preferredContactMethodType,
        title: data.title,
      };
      const state: BookingState = {
        ...bookingState,
        customerContact,
      };

      setBookingContext({
        ...state,
        customerContact,
      });
      nextStep(state);
    });
  };
  const onPrevious: WizardNavigate = (previousStep) => {
    previousStep(bookingState);
  };
  const onPrivacyStatementClick = (): void => {
    modalManager.open({
      title: 'Privacy',
      content: () => privacyStatementModal,
    });
  };

  const viewProps: CustomerContactPageViewProps = {
    formRef,
    initialFormData,
    onPrivacyStatementClick,
    prefixes,
    preferredContactMethods,
  };

  return (
    <Wizard currentStep={Steps.ContactDetails} onNext={onNext} onPrevious={onPrevious} disabled={isLoading}>
      {viewProvider.renderCustomerContactPage(viewProps)}
    </Wizard>
  );
});
